import { animate, style, transition, trigger } from '@angular/animations';
import { AsyncPipe, NgClass, NgIf, ViewportScroller } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  Signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { IconLinkWithCountComponent, SvgComponent } from '@app/base-components';
import { ButtonQaId, HabaQaId } from '@app/helpers';
import {
  CloudinaryService,
  CustomSiteContext,
  HeaderHeightService,
  OciService,
  ScrollService,
} from '@app/services';
import { Entry, EntryCollection } from 'contentful';
import { ContentfulRestService } from 'haba-contentful-components';
import {
  Navigation,
  NavigationNode,
  NavigationQuickCall,
} from 'haba-contentful-components/lib/interfaces/navigation';
import { AuthService, I18nModule, UrlModule, WindowRef } from 'haba-core';
import { PrefixboxService, WishlistService } from 'haba-emporix';
import { EMPTY, fromEvent, iif, Observable, of, Subject, Subscription } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  share,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { HabaMiniCartComponent } from '../cart/cart-shared/mini-cart/haba-mini-cart.component';
import { AbstractContentfulBase } from '../contentful/AbstractContentfulBase';
import { TopBannerComponent } from '../contentful/top-banner/top-banner.component';
import { HabaLoginComponent } from '../navigation/login/haba-login.component';
import { TopLinksComponent } from '../navigation/top-links/top-links.component';
import { SearchWrapperComponent } from '../search/search-wrapper/search-wrapper.component';
import { CountryPopupComponent } from './country-popup/country-popup.component';
import { HorizontalMenuComponent } from './horizontal-menu/horizontal-menu.component';
import { NavigationComponentService } from './navigation-component.service';
import { SideMenuComponent } from './side-menu/side-menu.component';
import { ShopNavMobileComponent } from './shop-nav-mobile/shop-nav-mobile.component';
import { SsrNavigationComponent } from './ssr-navigation/ssr-navigation.component';
@Component({
  selector: 'app-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  providers: [NavigationComponentService],

  imports: [
    CountryPopupComponent,
    TopBannerComponent,
    NgClass,
    TopLinksComponent,
    SvgComponent,
    NgIf,
    SearchWrapperComponent,
    IconLinkWithCountComponent,
    HabaLoginComponent,
    HabaMiniCartComponent,
    HorizontalMenuComponent,
    SideMenuComponent,
    AsyncPipe,
    UrlModule,
    I18nModule,
    ShopNavMobileComponent,
    SsrNavigationComponent,
    RouterLink,
  ],
  animations: [
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-6px)' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)' })),
      ]),
    ]),
    trigger('slideUpFade', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-6px)', height: '0', overflow: 'hidden' }),
        animate('200ms ease-out', style({ opacity: 1, transform: 'translateY(0)', height: '*' })),
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('150ms ease-in', style({ opacity: 0, transform: 'translateY(-6px)', height: '0' })),
      ]),
    ]),
  ],
})
export class HeaderComponent
  extends AbstractContentfulBase
  implements OnInit, AfterViewInit, OnDestroy
{
  @ViewChild('sideMenuContainer') sideMenuContainer!: ElementRef;
  @ViewChild('header') header!: ElementRef;
  @ViewChild('mobileSearchWrapper') mobileSearchWrapper!: ElementRef;
  @ViewChild('shopNavWrapper') shopNavWrapper!: ElementRef;
  @Input() isOrderConfirmation: boolean;

  get inScroll(): boolean {
    return this.headerHeightService.inScroll;
  }
  set inScroll(value: boolean) {
    this.headerHeightService.setInScroll(value);
  }
  baseId: HabaQaId;
  burgerButtonId: ButtonQaId;
  toggleSearchButtonId: ButtonQaId;
  logoButtonId: ButtonQaId;
  wishlistButtonId: ButtonQaId;
  isLoggedIn = false;
  searchToggled = false;
  sideMenuToggled = false;
  mobileTopOffset: number = 0;
  mobileTopOffsetInScroll: number = 0;

  navigationNodes$: Observable<Entry<NavigationNode>[]>;
  navigationData$: Observable<EntryCollection<Navigation>>;

  wehrfritzLogo = {
    id: '/wehrfritz/mainsources/logos/Wehrfritz_Logo.svg',
    width: 325,
    height: 80,
  };
  wehrfritzLogoSmall = {
    id: '/wehrfritz/mainsources/logos/Wehrfritz_Logo_small.svg',
    width: 30,
    height: 28,
  };
  wehrfritzLogoSrc: string;
  wehrfritzLogoSmallSrc: string;

  isComponentInitiated: boolean;

  totalWishlistItems$ = this.authService.isUserLoggedIn().pipe(
    switchMap((isLoggedIn) => iif(() => isLoggedIn, this.wishlistService.totalItems$, of(0))),
    distinctUntilChanged()
  );

  private subscriptions: Subscription[] = [];
  private unsubscribe$ = new Subject<void>();
  public categoryIds = new Set<string>();

  scrollEvent$ = fromEvent(window, 'scroll').pipe(
    takeUntil(this.unsubscribe$),
    map((event) => event.target),
    map((document: Document) => document.scrollingElement),
    share()
  );

  public isTop: Signal<boolean> = toSignal(
    this.scrollEvent$.pipe(
      takeUntil(this.unsubscribe$),
      map((scrollingElement) => scrollingElement.scrollTop),
      map((scrollPosition) => scrollPosition === 0),
      distinctUntilChanged()
    ),
    { initialValue: true }
  );

  constructor(
    private authService: AuthService,
    private contentfulRestService: ContentfulRestService,
    protected route: ActivatedRoute,
    private wishlistService: WishlistService,
    protected router: Router,
    protected siteContextService: CustomSiteContext,
    private cloudinaryService: CloudinaryService,
    private prefixboxService: PrefixboxService,
    protected ociService: OciService,
    private viewportScroller: ViewportScroller,
    private winRef: WindowRef,
    private scrollService: ScrollService,
    private headerHeightService: HeaderHeightService,
    private cdr: ChangeDetectorRef,
    @Inject(PLATFORM_ID) protected platform_id
  ) {
    super(router, siteContextService);
  }
  private oldDif = 0;
  private oldHeight = 0;
  private scrollUpAccumulator = 0;
  private readonly SCROLL_THRESHOLD = 80;

  ngOnInit() {
    this.isComponentInitiated = true;

    this.scrollEvent$.pipe(takeUntil(this.unsubscribe$)).subscribe((scroll) => {
      const scrollTop = scroll.scrollTop;

      if (this.oldHeight !== scroll.scrollHeight) {
        // Keep oldDif in sync so the next real scroll event starts from the right baseline.
        this.oldDif = scrollTop;
        this.oldHeight = scroll.scrollHeight;
        return;
      }

      if (scrollTop === 0) {
        if (this.inScroll) this.inScroll = false;
        this.scrollUpAccumulator = 0;
        this.oldDif = 0;
        return;
      }

      const delta = scrollTop - this.oldDif;

      if (delta > 0) {
        if (this.scrollUpAccumulator !== 0) this.scrollUpAccumulator = 0;
        if (!this.inScroll && scrollTop >= this.SCROLL_THRESHOLD) this.inScroll = true;
      } else if (delta < 0) {
        this.scrollUpAccumulator += Math.abs(delta);
        if (this.inScroll && this.scrollUpAccumulator >= this.SCROLL_THRESHOLD) {
          this.inScroll = false;
          this.scrollUpAccumulator = 0;
        }
      }

      this.oldDif = scrollTop;
    });

    this.router.events
      .pipe(
        takeUntil(this.unsubscribe$),
        filter((event) => event instanceof NavigationEnd),
        startWith(null)
      )
      .subscribe(() => {
        this.inScroll = false;
        this.scrollUpAccumulator = 0;
      });

    this.initIds();
    this.initUserCheck();
    this.wehrfritzLogoSrc = this.cloudinaryService.cloudinary.image(this.wehrfritzLogo.id).toURL();
    this.wehrfritzLogoSmallSrc = this.cloudinaryService.cloudinary
      .image(this.wehrfritzLogoSmall.id)
      .toURL();

    this.navigationData$ = this.contentfulRestService.getNavigation().pipe(
      switchMap((navigationData) => {
        navigationData.items.forEach((navigation) => {
          if (navigation.fields.navigationNodes) {
            this.fetchCategoryIds(navigation.fields.navigationNodes);
          }
        });
        return this.prefixboxService.getCategoryTreeRaw().pipe(map(() => navigationData));
      }),
      map((navigationData) => {
        navigationData.items.forEach((navigation) => {
          if (navigation.fields.navigationNodes) {
            this.addCategoryUrlsToNavigationNodes(navigation.fields.navigationNodes);
            navigation.fields.navigationNodes = this.transformNavigationNodes(
              navigation.fields.navigationNodes
            );
          }
          if (navigation.fields.quickCallComponents) {
            navigation.fields.quickCallComponents = this.transformNavigationQuickLinks(
              navigation.fields.quickCallComponents
            );
          }
        });
        return navigationData;
      })
    );
    this.navigationNodes$ = this.navigationData$.pipe(
      map((navigation) => this.getNavigationNodes(navigation?.items[0]?.fields?.navigationNodes)),
      share()
    );
  }

  ngAfterViewInit(): void {
    this.headerHeightService.headerHeight$
      .pipe(
        filter((value) => value > 0),
        take(1)
      )
      .subscribe((height) => {
        this.mobileTopOffset = height - (this.shopNavWrapper?.nativeElement?.offsetHeight || 0);
        this.mobileTopOffsetInScroll =
          height -
          (this.mobileSearchWrapper?.nativeElement?.offsetHeight || 0) -
          (this.shopNavWrapper?.nativeElement?.offsetHeight || 0);
        this.cdr.detectChanges();
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(($sub) => $sub.unsubscribe());

    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  initUserCheck() {
    this.subscriptions.push(
      this.authService
        .isUserLoggedIn()
        .pipe(
          tap((isLoggedIn) => (this.isLoggedIn = isLoggedIn)),
          switchMap((isLoggedIn) =>
            iif(() => isLoggedIn, this.wishlistService.getWishlists(), EMPTY)
          )
        )
        .subscribe()
    );
  }

  toggleSearch() {
    this.searchToggled = !this.searchToggled;
  }

  onCloseSearch() {
    this.searchToggled = false;
    this.sideMenuToggled = false;
    const body = document.getElementsByTagName('body')[0];
    if (!this.sideMenuToggled) body.classList.remove('modal-open');
    const headerSpacer = document.getElementsByClassName('header-spacer')[0];
    if (headerSpacer) (headerSpacer as HTMLElement).style.display = 'block';
  }

  toggleSideMenu() {
    this.sideMenuToggled = true;
    const body = document.getElementsByTagName('body')[0];
    if (this.sideMenuToggled) body.classList?.add('modal-open');
    const headerSpacer = document.getElementsByClassName('header-spacer')[0];
    if (headerSpacer) (headerSpacer as HTMLElement).style.display = 'none';
  }

  get isShopRelatedPage() {
    const urlSegments = this.router.url.split('/');
    return (
      urlSegments[1] === 'shop' ||
      urlSegments[1] === 'c' ||
      urlSegments[1] === 'p' ||
      urlSegments[1] === 'cart'
    );
  }

  onSearchResultClick() {
    this.searchToggled = false;
    if (this.sideMenuToggled) this.toggleSideMenu();
  }

  navigateHome(event: Event): void {
    event.preventDefault();
    this.scrollService.removePosition(this.zone + '/');
    this.router.navigate(['/'], { onSameUrlNavigation: 'reload' }).then(() => {
      this.viewportScroller.scrollToPosition([0, 0]);
    });
  }

  scrollSideMenuToTop() {
    this.sideMenuContainer?.nativeElement.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  get isBrowser(): boolean {
    return this.winRef.isBrowser();
  }

  get zone(): string {
    const [, zone] = this.winRef.document.location.pathname.split('/');

    return zone;
  }

  get isDesktop(): boolean {
    if (!this.isBrowser) return true;
    return this.winRef.nativeWindow.innerWidth > 991;
  }

  private addCategoryUrlsToNavigationNodes(
    entries: Entry<NavigationNode>[]
  ): Entry<NavigationNode>[] {
    entries.forEach((entry) => {
      if (entry.fields && (!entry.fields.url || entry.fields.url.trim().length === 0)) {
        const categoryId = entry.fields.categoryId?.split(':')[3];
        if (categoryId) {
          const category = this.prefixboxService.getCategoryById(categoryId);
          entry.fields['url'] = category?.url;
        }
      }
      if (entry.fields?.childNavigationNodes) {
        this.addCategoryUrlsToNavigationNodes(entry.fields.childNavigationNodes);
      }
    });
    return entries;
  }

  private transformNavigationNodes(entries: Entry<NavigationNode>[]): Entry<NavigationNode>[] {
    entries.forEach((entry) => {
      if (entry.fields) {
        let cloudinaryImage;
        if (Array.isArray(entry.fields?.cloudinaryImage)) {
          cloudinaryImage = entry.fields?.cloudinaryImage[0]?.public_id;
        }
        entry.fields = {
          ...entry.fields,
          cloudinaryImage,
        };
        if (entry.fields.childNavigationNodes) {
          entry.fields.childNavigationNodes.forEach((childEntry) => {
            let cloudinaryImage;
            if (Array.isArray(childEntry?.fields?.cloudinaryImage)) {
              cloudinaryImage = childEntry.fields.cloudinaryImage[0]?.public_id;
            }
            childEntry.fields = {
              ...childEntry.fields,
              cloudinaryImage,
            };
          });
        }

        let cloudinaryImage2;
        if (Array.isArray(entry.fields?.cloudinaryImage2)) {
          cloudinaryImage2 = entry.fields?.cloudinaryImage2[0]?.public_id;
        }
        entry.fields = {
          ...entry.fields,
          cloudinaryImage2,
        };
        if (entry.fields.childNavigationNodes) {
          entry.fields.childNavigationNodes.forEach((childEntry) => {
            let cloudinaryImage2;
            if (Array.isArray(childEntry?.fields?.cloudinaryImage2)) {
              cloudinaryImage2 = childEntry.fields.cloudinaryImage2[0]?.public_id;
            }
            childEntry.fields = {
              ...childEntry.fields,
              cloudinaryImage2,
            };
          });
        }
      }
    });
    return entries;
  }

  private transformNavigationQuickLinks(
    entries: Entry<NavigationQuickCall>[]
  ): Entry<NavigationQuickCall>[] {
    entries.forEach((entry) => {
      if (entry.fields) {
        let cloudinaryImage;
        if (Array.isArray(entry.fields?.cloudinaryImage)) {
          cloudinaryImage = entry.fields.cloudinaryImage[0]?.public_id;
        }
        entry.fields = {
          ...entry.fields,
          cloudinaryImage,
        };
      }
    });
    return entries;
  }

  private initIds(): void {
    this.baseId = HabaQaId.forPage(HabaQaId.page.header);
    this.logoButtonId = this.baseId.getButtonId(HabaQaId.elements.button.logo);
    this.wishlistButtonId = this.baseId.getButtonId(HabaQaId.elements.button.wishlist);
    this.burgerButtonId = this.baseId.getButtonId(HabaQaId.elements.button.burger);
    this.toggleSearchButtonId = this.baseId.getButtonId(HabaQaId.elements.button.toggleSearch);
  }

  fetchCategoryIds(entries: Entry<NavigationNode>[]): void {
    entries.forEach((entry) => {
      if (entry.fields) {
        const categoryId = entry.fields.categoryId?.split(':')[3];
        if (!entry.fields.url && categoryId) {
          this.categoryIds.add(categoryId);
        }
        if (entry.fields?.childNavigationNodes) {
          this.fetchCategoryIds(entry.fields.childNavigationNodes);
        }
      }
    });
  }

  getNavigationNodes(navigationNodes: Entry<NavigationNode>[]): Entry<NavigationNode>[] {
    if (!this.ociService.isOciSession) {
      return navigationNodes;
    }

    const nodes = navigationNodes.filter((node) =>
      this.ociService.isNavigationNodeAllowed(node.fields?.name)
    );

    return nodes?.length ? nodes : navigationNodes;
  }
}
